import { useMemo, useState, type ReactNode } from 'react'
import { Button } from './button'
import { Icon } from './icon'
import { EmptyState } from './empty-state'
import { Input } from './field'
import { cn } from '../cn'

export type Column<Row> = {
  key: string
  header: string
  /** Hücrede gösterilecek içerik. */
  cell: (row: Row) => ReactNode
  /** Sıralama ve arama için ham değer. Verilmezse kolon sıralanamaz. */
  value?: (row: Row) => string | number | null
  align?: 'left' | 'right'
}

export type DataTableProps<Row> = {
  rows: Row[]
  columns: Column<Row>[]
  rowKey: (row: Row) => string
  /** İstemci tarafı arama; sunucu araması gerektiğinde dışarıdan yönetilir. */
  searchPlaceholder?: string
  pageSize?: number
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: ReactNode
  /** Dışa aktarım dosya adı; verilmezse düğme gösterilmez. */
  exportFileName?: string
}

function toCsv<Row>(rows: Row[], columns: Column<Row>[]): string {
  const escape = (value: unknown) => `"${String(value ?? '').replaceAll('"', '""')}"`
  const header = columns.map((column) => escape(column.header)).join(',')
  const body = rows.map((row) =>
    columns.map((column) => escape(column.value?.(row) ?? '')).join(','),
  )
  return [header, ...body].join('\n')
}

/**
 * Sıralama, arama, sayfalama ve CSV dışa aktarımı olan tablo.
 *
 * Sıralama ve arama İSTEMCİ tarafında: dernek panelindeki listeler (kurullar,
 * üyelik tipleri, sağlayıcılar) yüzler mertebesinde. Binlerce satırlık üye
 * listesi geldiğinde sunucu tarafı sayfalama ayrı bir bileşen olacak — bunu
 * şimdi genelleştirmek, kullanılmayan bir soyutlama üretirdi.
 */
export function DataTable<Row>({
  rows,
  columns,
  rowKey,
  searchPlaceholder = 'Ara…',
  pageSize = 20,
  emptyTitle = 'Kayıt yok',
  emptyDescription,
  emptyAction,
  exportFileName,
}: DataTableProps<Row>) {
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [page, setPage] = useState(0)

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('tr')
    if (!needle) return rows
    return rows.filter((row) =>
      columns.some((column) =>
        String(column.value?.(row) ?? '')
          .toLocaleLowerCase('tr')
          .includes(needle),
      ),
    )
  }, [rows, columns, query])

  const sorted = useMemo(() => {
    if (!sort) return filtered
    const column = columns.find((item) => item.key === sort.key)
    if (!column?.value) return filtered
    const factor = sort.direction === 'asc' ? 1 : -1
    return [...filtered].sort((left, right) => {
      const a = column.value?.(left) ?? ''
      const b = column.value?.(right) ?? ''
      if (typeof a === 'number' && typeof b === 'number') return (a - b) * factor
      // Türkçe sıralama: "İstanbul" ile "Izmir" doğru sırada çıksın.
      return String(a).localeCompare(String(b), 'tr') * factor
    })
  }, [filtered, columns, sort])

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
  const current = Math.min(page, pageCount - 1)
  const visible = sorted.slice(current * pageSize, current * pageSize + pageSize)

  function toggleSort(key: string) {
    setSort((previous) =>
      previous?.key === key
        ? { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    )
  }

  function exportCsv() {
    const blob = new Blob([`﻿${toCsv(sorted, columns)}`], {
      type: 'text/csv;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${exportFileName}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setPage(0)
          }}
          placeholder={searchPlaceholder}
          className="max-w-xs"
          aria-label={searchPlaceholder}
        />
        {exportFileName && (
          <Button variant="secondary" size="sm" onClick={exportCsv} className="ml-auto">
            <Icon name="download" size={16} aria-hidden />
            CSV
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--ad-border)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--ad-surface-muted)] text-left text-xs text-[var(--ad-text-muted)]">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn('px-4 py-2 font-medium', column.align === 'right' && 'text-right')}
                >
                  {column.value ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="inline-flex items-center gap-1 hover:text-[var(--ad-text)]"
                      aria-label={`${column.header} sütununa göre sırala`}
                    >
                      {column.header}
                      {sort?.key !== column.key ? (
                        <Icon name="chevrons-up-down" size={14} aria-hidden />
                      ) : sort.direction === 'asc' ? (
                        <Icon name="arrow-up" size={14} aria-hidden />
                      ) : (
                        <Icon name="arrow-down" size={14} aria-hidden />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={rowKey(row)} className="border-t border-[var(--ad-border)]">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn('px-4 py-2.5', column.align === 'right' && 'text-right')}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && (
          <EmptyState
            title={query ? 'Aramanıza uyan kayıt yok' : emptyTitle}
            description={query ? 'Farklı bir arama deneyin.' : emptyDescription}
            action={query ? undefined : emptyAction}
          />
        )}
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between text-xs text-[var(--ad-text-muted)]">
          <span>
            {sorted.length} kayıt · sayfa {current + 1}/{pageCount}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={current === 0}
              onClick={() => setPage(current - 1)}
            >
              Önceki
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={current >= pageCount - 1}
              onClick={() => setPage(current + 1)}
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
